/** Dashboard figures by period, their time series and the owner's monthly
 *  plan. Backend: apps/tenants/dashboard_api.py. */
import { get } from "svelte/store";
import { locale } from "svelte-i18n";
import { api } from "./api";
import { formatDayFirst, formatEur } from "./format";

export type Period = "day" | "week" | "month" | "quarter" | "half" | "year" | "total";
export const PERIODS: Period[] = ["day", "week", "month", "quarter", "half", "year", "total"];
export const PERIOD_LABEL_KEYS: Record<Period, string> = {
  day: "dashboard.period.day",
  week: "dashboard.period.week",
  month: "dashboard.period.month",
  quarter: "dashboard.period.quarter",
  half: "dashboard.period.half",
  year: "dashboard.period.year",
  total: "dashboard.period.total",
};

export const METRICS = [
  "clients",
  "suppliers",
  "requests_sent",
  "requests_received",
  "products",
  "orders_count",
  "orders_sum",
  "invoices_count",
  "invoices_sum",
] as const;
export type Metric = (typeof METRICS)[number];
export const MONEY_METRICS: readonly Metric[] = ["orders_sum", "invoices_sum"];
export const METRIC_LABEL_KEYS: Record<Metric, string> = {
  clients: "dashboard.metric.clients",
  suppliers: "dashboard.metric.suppliers",
  requests_sent: "dashboard.metric.requests_sent",
  requests_received: "dashboard.metric.requests_received",
  products: "dashboard.metric.products",
  orders_count: "dashboard.metric.orders_count",
  orders_sum: "dashboard.metric.orders_sum",
  invoices_count: "dashboard.metric.invoices_count",
  invoices_sum: "dashboard.metric.invoices_sum",
};

/** The six dashboard blocks; a pair (sent/received, count/amount) shares one. */
export type Block = {
  id: string;
  labelKey: string;
  icon: string;
  parts: { metric: Metric; partKey: string }[];
};
export const BLOCKS: Block[] = [
  { id: "clients", labelKey: "dashboard.block.clients", icon: "groups", parts: [{ metric: "clients", partKey: "dashboard.part.new" }] },
  { id: "suppliers", labelKey: "dashboard.block.suppliers", icon: "local_shipping", parts: [{ metric: "suppliers", partKey: "dashboard.part.new" }] },
  {
    id: "requests",
    labelKey: "dashboard.block.requests",
    icon: "handshake",
    parts: [
      { metric: "requests_sent", partKey: "dashboard.part.sent" },
      { metric: "requests_received", partKey: "dashboard.part.received" },
    ],
  },
  { id: "products", labelKey: "dashboard.block.products", icon: "inventory_2", parts: [{ metric: "products", partKey: "dashboard.part.new" }] },
  {
    id: "orders",
    labelKey: "dashboard.block.orders",
    icon: "shopping_cart",
    parts: [
      { metric: "orders_count", partKey: "dashboard.part.count" },
      { metric: "orders_sum", partKey: "dashboard.part.sum" },
    ],
  },
  {
    id: "invoices",
    labelKey: "dashboard.block.invoices",
    icon: "request_quote",
    parts: [
      { metric: "invoices_count", partKey: "dashboard.part.count" },
      { metric: "invoices_sum", partKey: "dashboard.part.sum" },
    ],
  },
];

export type Figure = { actual: number; goal: number | null; pct: number | null };
export type DashboardStats = {
  period: Period;
  start: string;
  end: string;
  today: string;
  can_edit_plan: boolean;
  metrics: Record<Metric, Figure>;
};
export type SeriesBucket = {
  start: string;
  end: string;
  values: Record<Metric, number>;
  goals: Record<Metric, number | null>;
};
export type DashboardSeries = { period: Period; granularity: Period; buckets: SeriesBucket[] };
export type PlanMonth = {
  month: string;
  targets: Record<Metric, number | null>;
  actuals: Record<Metric, number | null> | null;
  closed: boolean;
  recorded: boolean;
  editable: boolean;
  updated_at: string | null;
};
export type DashboardPlan = {
  year: number;
  current_month: string;
  can_edit: boolean;
  metrics: Metric[];
  months: PlanMonth[];
};

export function fetchDashboardStats(slug: string, period: Period, anchor: string): Promise<DashboardStats> {
  return api.get<DashboardStats>(`/t/${slug}/dashboard/stats?period=${period}&anchor=${anchor}`);
}

export function fetchDashboardSeries(slug: string, period: Period, anchor: string): Promise<DashboardSeries> {
  return api.get<DashboardSeries>(`/t/${slug}/dashboard/series?period=${period}&anchor=${anchor}`);
}

export function fetchDashboardPlan(slug: string, year: number): Promise<DashboardPlan> {
  return api.get<DashboardPlan>(`/t/${slug}/dashboard/plan?year=${year}`);
}

export function saveDashboardPlan(
  slug: string,
  month: string,
  targets: Record<Metric, number | null>,
): Promise<PlanMonth> {
  return api.put<PlanMonth>(`/t/${slug}/dashboard/plan/${month}`, { targets });
}

/** A figure as text: money in whole euros (compact €1.6k on tiles), counts
 *  with at most one decimal — a day's share of a monthly plan can be 0.3. */
export function formatFigure(metric: Metric, value: number, compact = false): string {
  if (MONEY_METRICS.includes(metric)) {
    if (compact && Math.abs(value) >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`;
    if (compact && Math.abs(value) >= 1_000) return `€${(value / 1_000).toFixed(1)}k`;
    return formatEur(value, 0);
  }
  return new Intl.NumberFormat(get(locale) ?? "en", { maximumFractionDigits: 1 }).format(value);
}

/** Move a YYYY-MM-DD anchor one period back (-1) or forward (+1). UTC date
 *  arithmetic: the anchor is a company calendar day, not an instant. */
export function shiftAnchor(period: Period, anchor: string, step: -1 | 1): string {
  const d = new Date(`${anchor}T00:00:00Z`);
  if (period === "day") d.setUTCDate(d.getUTCDate() + step);
  else if (period === "week") d.setUTCDate(d.getUTCDate() + 7 * step);
  else if (period !== "total") {
    const months = { month: 1, quarter: 3, half: 6, year: 12 }[period];
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + months * step);
  }
  return d.toISOString().slice(0, 10);
}

type Translate = (id: string, options?: { values?: Record<string, string | number> }) => string;

function formatDay(iso: string, options: Intl.DateTimeFormatOptions): string {
  const date = new Date(`${iso}T00:00:00Z`);
  // A label with a day is written day first; «сентябрь 2026» is left to the language.
  if (options.day) return formatDayFirst(date, options, get(locale), "UTC");
  return new Intl.DateTimeFormat(get(locale) ?? "en", { ...options, timeZone: "UTC" }).format(date);
}

/** "September 2026", "Q3 2026", "14.09 – 20.09.2026" … for the period toolbar. */
export function periodLabel(period: Period, start: string, end: string, t: Translate): string {
  const year = start.slice(0, 4);
  const month = Number(start.slice(5, 7));
  switch (period) {
    case "day":
      return formatDay(start, { day: "2-digit", month: "2-digit", year: "numeric" });
    case "week":
      return `${formatDay(start, { day: "2-digit", month: "2-digit" })} – ${formatDay(end, { day: "2-digit", month: "2-digit", year: "numeric" })}`;
    case "month":
      return formatDay(start, { month: "long", year: "numeric" });
    case "quarter":
      return t("dashboard.period.quarterLabel", { values: { n: Math.floor((month - 1) / 3) + 1, year } });
    case "half":
      return t("dashboard.period.halfLabel", { values: { n: month <= 6 ? 1 : 2, year } });
    case "year":
      return year;
    default:
      return t("dashboard.period.allTime");
  }
}

/** Short axis label of one bucket in «Динамика». */
export function bucketLabel(granularity: Period, start: string, t: Translate): string {
  const year = start.slice(2, 4);
  const month = Number(start.slice(5, 7));
  switch (granularity) {
    case "day":
    case "week":
      return formatDay(start, { day: "2-digit", month: "2-digit" });
    case "month":
      return `${formatDay(start, { month: "short" })} ${year}`;
    case "quarter":
      return t("dashboard.period.quarterLabel", { values: { n: Math.floor((month - 1) / 3) + 1, year } });
    case "half":
      return t("dashboard.period.halfLabel", { values: { n: month <= 6 ? 1 : 2, year } });
    default:
      return start.slice(0, 4);
  }
}

/** Month name for the plan's month chips. */
export function monthName(month: string): string {
  return formatDay(`${month}-01`, { month: "short" });
}
